/**
 * ProfilePage — K. 사용자프로파일 > 내 정보 (내 정보 수정)
 * J-1-3 비밀번호 변경 팝업 포함
 *
 * 화면 형식은 V2.6 기획서 K. 내 정보(프로필 카드 · 개인정보/업체정보 2열 · 내 소개 · 하단 고정 저장 바),
 * 항목은 2026-10-12 개선사항 md 기준.
 * - C-16 '내 프로필 홍보' 메뉴를 없애고 내소개 속성(로고·한줄소개·태그·소개메모)을 '내 소개' 섹션으로 통합
 *   (사장님만). 데이터 원장은 그대로 TS_COMPANY_PROMO / TS_COMPANY_PROMO_TAG(company-promo API).
 * - C-17 시공가능지역(AREA 태그)은 시군구 목록에서 5개 이내 선택, 사장님 자유메모 = 소개메모(INTRO_TEXT).
 * - C-19 업체정보에 사업자 주소(주소 검색 + 상세주소) 추가.
 * - 변경은 하단 저장 바 하나로 모아 저장한다(로고도 [저장] 때 반영).
 */
import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Alert, Avatar, Button, Card, FileInput, FilterChip, Input, Modal, MultiSelect, Page, PageHeader, Skeleton, Textarea } from '@indipia/ui-core';
import { fetchSession, useAuthStore, SIGUNGU_LIST } from '@indipia/shared';
import {
  fetchProfile,
  updatePersonalInfo,
  updateCompanyInfo,
  changePassword,
  requestPhoneOtp,
  verifyPhoneOtp,
  type UserProfile,
} from '../api/profile';
import {
  fetchCompanyPromo,
  updateCompanyPromo,
  addCompanyPromoTag,
  deleteCompanyPromoTag,
  deleteLogo,
  type CompanyPromoResponse,
} from '../api/company-promo';
import { fetchImageBlobUrl } from '../api/files';
import { checkUserExists } from '../api/member-auth';
import { AddressSearchField } from '../components/AddressSearchField';

// 로고는 sv-member 자체 프록시 경로(게이트웨이 JWT 필요)라 <img src>에 바로 못 쓴다 —
// apiClient(JWT)로 blob 받아 objectURL로 표시한다. 로고 URL은 바꿔 올려도 같으므로 version으로 다시 받는다.
function useImageBlobUrl(url: string | null | undefined, version = 0): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!url) { setBlobUrl(null); return; }
    let cancelled = false;
    let objectUrl: string | null = null;
    fetchImageBlobUrl(version ? `${url}?v=${version}` : url).then((u) => {
      if (cancelled) { URL.revokeObjectURL(u); return; }
      objectUrl = u;
      setBlobUrl(u);
    }).catch(() => setBlobUrl(null));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, version]);
  return blobUrl;
}

// 내 소개 태그 — 코드값(SPECIALTY·STYLE·AREA)은 DB·API 그대로, 라벨은 개선사항 A 기준
// (전문분야 → 시공분야, 시공스타일 → 디자인스타일). 개선사항 B에 따라 자유 입력 대신 목록에서 고른다.
type TagType = 'SPECIALTY' | 'STYLE' | 'AREA';
const TAG_TYPES: TagType[] = ['SPECIALTY', 'STYLE', 'AREA'];
const SPECIALTY_OPTIONS = ['아파트 전체 리모델링', '주택·빌라 리모델링', '욕실·주방 부분시공', '상가·사무실 인테리어', '신축·전원주택', '누수·하자 보수'];
const STYLE_OPTIONS = ['모던', '미니멀', '내추럴(우드)', '북유럽', '클래식', '인더스트리얼'];
const STYLE_MAX = 3;
const AREA_MAX = 5;
const MEMO_MAX = 300;
const AREA_OPTIONS = SIGUNGU_LIST.map((s) => ({ value: s.name, label: s.name }));
const AREA_BY_NAME = new Map(SIGUNGU_LIST.map((s) => [s.name, s]));

type TagDraft = Record<TagType, string[]>;

function tagsOf(promo: CompanyPromoResponse | undefined, type: string): string[] {
  return (promo?.tags ?? []).filter((t) => t.tagType === type).map((t) => t.value);
}

function tagDraftOf(promo: CompanyPromoResponse | undefined): TagDraft {
  return { SPECIALTY: tagsOf(promo, 'SPECIALTY'), STYLE: tagsOf(promo, 'STYLE'), AREA: tagsOf(promo, 'AREA') };
}

/** 시공가능지역 포함 관계 — '전국 전체'는 모든 지역, 'OO 전체'는 같은 시·도의 시·구를 덮는다 */
function isNationwide(name: string) { return AREA_BY_NAME.get(name)?.sido === '전국'; }
function isSidoAll(name: string) { const a = AREA_BY_NAME.get(name); return !!a && a.sido !== '전국' && a.sigungu === '전체'; }

function coveredBy(selected: string[], name: string): boolean {
  const a = AREA_BY_NAME.get(name);
  if (!a || a.sido === '전국') return false;
  return selected.some((s) => s !== name && (isNationwide(s) || (a.sigungu !== '전체' && isSidoAll(s) && AREA_BY_NAME.get(s)?.sido === a.sido)));
}

/** V2.6 규칙: 전체를 고르면 그 안의 지역은 빠지고, 안의 지역을 고르면 덮던 전체가 빠진다 */
function normalizeAreas(prev: string[], next: string[]): string[] {
  const added = next.find((v) => !prev.includes(v));
  if (!added) return next;
  const a = AREA_BY_NAME.get(added);
  if (!a) return next;
  if (a.sido === '전국') return [added];
  return next.filter((v) => {
    if (v === added) return true;
    if (isNationwide(v)) return false;
    const o = AREA_BY_NAME.get(v);
    if (!o || o.sido !== a.sido) return true;
    return a.sigungu === '전체' ? false : o.sigungu !== '전체';
  });
}

// 프로필·홍보 데이터에서 폼 초기값을 만든다
interface FormState {
  memberName: string;
  mobile: string;
  compPhone: string;
  compEmail: string;
  compAddress: string;
  compAddressDtl: string;
  promoTagline: string;
  introMemo: string;
  tags: TagDraft;
}

function formOf(profile: UserProfile | undefined, promo: CompanyPromoResponse | undefined): FormState {
  return {
    memberName:     profile?.memberName ?? '',
    mobile:         profile?.mobile ?? '',
    compPhone:      profile?.compPhone ?? '',
    compEmail:      profile?.compEmail ?? '',
    compAddress:    profile?.compAddress ?? '',
    compAddressDtl: profile?.compAddressDtl ?? '',
    promoTagline:   promo?.promoTagline ?? '',
    introMemo:      promo?.intro ?? '',
    tags:           tagDraftOf(promo),
  };
}

const PERSONAL_KEYS = ['memberName', 'mobile'] as const;
const COMPANY_KEYS  = ['compPhone', 'compEmail', 'compAddress', 'compAddressDtl'] as const;
const PROMO_KEYS    = ['promoTagline', 'introMemo'] as const;

function sameList(a: string[], b: string[]) { return a.length === b.length && a.every((v, i) => v === b[i]); }

/** 프로필 저장 후 재로그인 없이 세션 갱신 — LNB·홈 등 useAuthStore.user 참조 화면에 즉시 반영 */
async function refreshAuthUser() {
  const { accessToken, refreshToken, user } = useAuthStore.getState();
  if (!accessToken || !refreshToken) return;
  try {
    const session = await fetchSession();
    useAuthStore.getState().setAuth({
      accessToken,
      refreshToken,
      user: {
        memberId:   session.memberId,
        memberName: session.memberName,
        compId:     session.compId   ?? user?.compId   ?? 0,
        compName:   session.compName ?? user?.compName ?? '',
        masterYn:   session.masterYn,
      },
    });
  } catch {
    // 실패해도 다음 로그인 시 정상 값으로 보정됨
  }
}

// ── 헬퍼 ──────────────────────────────────────────────────────────────────────

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.startsWith('02')) {
    const d = digits.slice(0, 10);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function calcPasswordStrength(pw: string): 0 | 1 | 2 | 3 {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[a-zA-Z]/.test(pw) && /[0-9]/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  return score as 0 | 1 | 2 | 3;
}

const STRENGTH_LABEL = ['입력 전', '약함', '보통', '강함'];
const STRENGTH_COLOR = [
  'var(--shell-border)',
  'var(--color-error)',
  'var(--color-warning)',
  'var(--color-success)',
];

// ── 비밀번호 변경 모달 (J-1-3) ───────────────────────────────────────────────

function PasswordChangeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [currentPw, setCurrentPw]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const navigate = useNavigate();
  const logout   = useAuthStore((s) => s.logout);

  const strength = calcPasswordStrength(newPw);

  useEffect(() => {
    if (!open) {
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setShowCurrent(false); setShowNew(false); setShowConfirm(false);
      setError(null); setConfirmError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => changePassword({
      currentPassword: currentPw,
      newPassword: newPw,
      confirmPassword: confirmPw,
    }),
    onSuccess: () => {
      // 서버가 전체 기기 Refresh 토큰을 폐기했으므로 로컬 토큰도 버리고 재로그인을 유도한다.
      toast.success('비밀번호가 변경됐어요. 변경된 비밀번호로 다시 로그인해주세요');
      onClose();
      logout();
      navigate('/login', { replace: true });
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
      setError(data?.message ?? '비밀번호 변경에 실패했습니다. 다시 시도해주세요.');
    },
  });

  function handleConfirmBlur() {
    if (confirmPw && newPw !== confirmPw) {
      setConfirmError('새 비밀번호가 일치하지 않습니다.');
    } else {
      setConfirmError(null);
    }
  }

  function handleSubmit() {
    if (!currentPw) { setError('현재 비밀번호를 입력해주세요.'); return; }
    if (newPw.length < 8) { setError('새 비밀번호는 8자 이상이어야 합니다.'); return; }
    if (newPw !== confirmPw) return; // blur에서 이미 표시됨
    setError(null);
    mutation.mutate();
  }

  function EyeBtn({ show, onToggle }: { show: boolean; onToggle: () => void }) {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={onToggle}
        aria-label={show ? '비밀번호 숨기기' : '비밀번호 표시'}
        aria-pressed={show}
        className="!absolute !right-2 !top-1/2 !-translate-y-1/2 !h-8 !w-8 !rounded-full !p-0"
        style={{ color: 'var(--shell-on-surface-2)' }}
      >
        {show ? (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        )}
      </Button>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="새 비밀번호 설정">
      <p className="mb-5 text-sm text-text-muted">새로 사용할 비밀번호를 입력해 주세요.</p>

      <div className="space-y-3">
        {/* 현재 비밀번호 */}
        <div className="relative">
          <Input
            type={showCurrent ? 'text' : 'password'}
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            placeholder="현재 비밀번호"
            className="pr-10"
          />
          <EyeBtn show={showCurrent} onToggle={() => setShowCurrent((v) => !v)} />
        </div>

        {/* 새 비밀번호 */}
        <div className="relative">
          <Input
            type={showNew ? 'text' : 'password'}
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            placeholder="8자 이상 · 영문 + 숫자 + 특수문자"
            className="pr-10"
          />
          <EyeBtn show={showNew} onToggle={() => setShowNew((v) => !v)} />
        </div>

        {/* 강도 인디케이터 */}
        {newPw && (
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-1.5 w-8 rounded-full transition-colors"
                  style={{ background: i <= strength ? STRENGTH_COLOR[strength] : 'var(--shell-border)' }}
                />
              ))}
            </div>
            <span className="text-xs font-semibold" style={{ color: STRENGTH_COLOR[strength] }}>
              강도: {STRENGTH_LABEL[strength]}
            </span>
          </div>
        )}

        {/* 새 비밀번호 확인 */}
        <div>
          <div className="relative">
            <Input
              type={showConfirm ? 'text' : 'password'}
              value={confirmPw}
              onChange={(e) => { setConfirmPw(e.target.value); setConfirmError(null); }}
              onBlur={handleConfirmBlur}
              placeholder="비밀번호를 다시 입력하세요"
              className="pr-10"
              error={confirmError ?? undefined}
            />
            <EyeBtn show={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />
          </div>
        </div>

        <p className="text-caption text-text-muted">최소 8자, 영문+숫자+특수문자 포함</p>

        {error && <Alert kind="error">{error}</Alert>}

        <div className="flex gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} className="flex-1">취소</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={mutation.isPending}
            loading={mutation.isPending}
            className="flex-1"
          >
            변경 완료
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── 화면 부품 ─────────────────────────────────────────────────────────────────

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** 수정할 수 없는 항목 — 잠금 아이콘 표시(V2.6 K. 내 정보). ui-core Input(disabled) 위에 아이콘만 겹친다 */
function LockedField({ label, value }: { label: string; value: string }) {
  return (
    <div className="relative">
      <Input label={label} value={value} disabled readOnly size="md" width="auto" />
      <span className="pointer-events-none absolute bottom-0 right-04 flex h-[var(--control-height-base)] items-center text-text-disabled">
        <LockIcon />
      </span>
    </div>
  );
}

/** 칩 선택 — 목록에 없는 기존 값(자유 입력 시절 값)도 칩으로 남겨 뺄 수 있게 한다 */
function ChipSelect({ label, sub, options, values, onToggle, warn }: {
  label: string;
  sub: ReactNode;
  options: string[];
  values: string[];
  onToggle: (value: string) => void;
  warn?: string | null;
}) {
  const all = [...options, ...values.filter((v) => !options.includes(v))];
  return (
    <div className="field field-auto">
      <label>{label} <span className="ml-02 font-normal text-text-muted">{sub}</span></label>
      <div className="flex flex-wrap gap-03">
        {all.map((v) => (
          <FilterChip key={v} size="lg" active={values.includes(v)} aria-pressed={values.includes(v)} onClick={() => onToggle(v)}>
            {v}
          </FilterChip>
        ))}
      </div>
      {warn && <span className="text-caption text-warning">{warn}</span>}
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

export function ProfilePage() {
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfile,
  });

  const isMaster = profile?.masterYn === 'Y';
  const isSns    = profile?.snsYn === 'Y';

  // 내 소개(C-16) — 사장님만. 업체 단위 데이터라 직원 계정에는 노출하지 않는다.
  const { data: promo, isLoading: promoLoading } = useQuery({
    queryKey: ['company-promo'],
    queryFn: fetchCompanyPromo,
    enabled: isMaster,
  });

  // 폼 값과 기준값(서버에 저장된 값). 변경 건수는 둘을 비교해 센다.
  const [form, setForm] = useState<FormState>(() => formOf(undefined, undefined));
  const [base, setBase] = useState<FormState>(() => formOf(undefined, undefined));
  // 사업자 주소(C-19) — 주소 검색으로 고른 좌표. 주소를 바꿀 때만 함께 보낸다.
  const [compGps, setCompGps] = useState<{ lat: number | null; lng: number | null } | null>(null);

  // 로고 — 고르거나 지운 것은 [저장] 때 반영한다
  const [logoFile, setLogoFile]       = useState<File | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoBlobUrl  = useImageBlobUrl(isMaster ? promo?.logoUrl : null, logoVersion);
  const shownLogo    = logoPreview ?? (logoRemoved ? null : logoBlobUrl);

  useEffect(() => {
    if (!logoFile) { setLogoPreview(null); return; }
    const u = URL.createObjectURL(logoFile);
    setLogoPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [logoFile]);

  // 휴대폰번호 변경 — 중복확인(blur) + OTP 인증
  const [mobileDupState, setMobileDupState] = useState<'idle' | 'checking' | 'ok' | 'duplicate'>('idle');
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [otpError,    setOtpError]    = useState<string | null>(null);
  const [otpCode,     setOtpCode]     = useState('');

  const [nameError,   setNameError]   = useState<string | null>(null);
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [areaError,   setAreaError]   = useState<string | null>(null);
  const [styleWarn,   setStyleWarn]   = useState<string | null>(null);
  const [saveError,   setSaveError]   = useState<string | null>(null);
  const [justSaved,   setJustSaved]   = useState(false);
  const styleWarnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showPwModal, setShowPwModal] = useState(false);

  // 폼은 데이터가 처음 올 때와 저장 직후에만 서버 값으로 맞춘다 — 캐시 갱신·창 포커스 재조회가
  // 입력 중인 값을 덮어쓰지 않게 한다.
  const profileSynced = useRef(false);
  const promoSynced   = useRef(false);

  function syncProfile(p: UserProfile, target: 'both' | 'base') {
    const f = formOf(p, undefined);
    const part = Object.fromEntries([...PERSONAL_KEYS, ...COMPANY_KEYS].map((k) => [k, f[k]])) as Partial<FormState>;
    setBase((prev) => ({ ...prev, ...part }));
    if (target === 'both') { setForm((prev) => ({ ...prev, ...part })); setCompGps(null); }
  }

  function syncPromo(p: CompanyPromoResponse, target: 'both' | 'base') {
    const part: Partial<FormState> = { promoTagline: p.promoTagline ?? '', introMemo: p.intro ?? '', tags: tagDraftOf(p) };
    setBase((prev) => ({ ...prev, ...part }));
    if (target === 'both') setForm((prev) => ({ ...prev, ...part }));
  }

  useEffect(() => {
    if (profile && !profileSynced.current) { profileSynced.current = true; syncProfile(profile, 'both'); }
  }, [profile]);

  useEffect(() => {
    if (promo && !promoSynced.current) { promoSynced.current = true; syncPromo(promo, 'both'); }
  }, [promo]);

  useEffect(() => () => { if (styleWarnTimer.current) clearTimeout(styleWarnTimer.current); }, []);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setSaveError(null);
  }

  function setTags(type: TagType, values: string[]) {
    setForm((f) => ({ ...f, tags: { ...f.tags, [type]: values } }));
    setSaveError(null);
  }

  // ── 변경 감지 ──
  const personalDirty = PERSONAL_KEYS.some((k) => form[k] !== base[k]);
  const companyDirty  = isMaster && COMPANY_KEYS.some((k) => form[k] !== base[k]);
  const promoDirty    = isMaster && (
    PROMO_KEYS.some((k) => form[k] !== base[k]) || TAG_TYPES.some((t) => !sameList(form.tags[t], base.tags[t]))
  );
  const logoDirty     = isMaster && (!!logoFile || (logoRemoved && !!promo?.logoUrl));

  const changedCount =
    PERSONAL_KEYS.filter((k) => form[k] !== base[k]).length +
    (isMaster
      ? COMPANY_KEYS.filter((k) => form[k] !== base[k]).length +
        PROMO_KEYS.filter((k) => form[k] !== base[k]).length +
        TAG_TYPES.filter((t) => !sameList(form.tags[t], base.tags[t])).length +
        (logoDirty ? 1 : 0)
      : 0);

  const mobileDigits  = form.mobile.replace(/\D/g, '');
  const mobileChanged = form.mobile.trim() !== base.mobile.trim();
  const mobileReadyForOtp = mobileChanged && mobileDigits.length >= 10 && mobileDupState !== 'duplicate';

  // ── 내 소개 선택 ──
  function toggleSpecialty(v: string) {
    const cur = form.tags.SPECIALTY;
    setTags('SPECIALTY', cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  }

  function toggleStyle(v: string) {
    const cur = form.tags.STYLE;
    if (cur.includes(v)) { setTags('STYLE', cur.filter((x) => x !== v)); return; }
    if (cur.length >= STYLE_MAX) {
      setStyleWarn(`디자인스타일은 최대 ${STYLE_MAX}개까지 고를 수 있어요`);
      if (styleWarnTimer.current) clearTimeout(styleWarnTimer.current);
      styleWarnTimer.current = setTimeout(() => setStyleWarn(null), 2000);
      return;
    }
    setTags('STYLE', [...cur, v]);
  }

  function handleAreaChange(values: string[]) {
    const prev = form.tags.AREA;
    const next = normalizeAreas(prev, values);
    // 추가할 때만 막는다 — 자유 입력 시절 5개를 넘게 저장된 회사도 빼는 건 할 수 있어야 한다
    if (next.length > AREA_MAX && next.length > prev.length) {
      setAreaError(`시공가능지역은 최대 ${AREA_MAX}개까지 고를 수 있어요`);
      return;
    }
    setAreaError(null);
    setTags('AREA', next);
  }

  // 시군구 목록에 없는 기존 시공가능지역(자유 입력 시절 값)도 선택지에 남겨 칩으로 보이게 한다
  const areaOptions = useMemo(() => {
    const legacy = form.tags.AREA.filter((v) => !AREA_BY_NAME.has(v)).map((v) => ({ value: v, label: v }));
    return [...legacy, ...AREA_OPTIONS];
  }, [form.tags.AREA]);

  // ── 로고 ──
  function handleLogoSelected(file: File | undefined) {
    if (!file) return;
    setLogoFile(file);
    setLogoRemoved(false);
    setSaveError(null);
  }

  function handleLogoDelete() {
    setLogoFile(null);
    setLogoRemoved(true);
    setSaveError(null);
  }

  // ── 휴대폰번호 ──
  function resetOtp() {
    setMobileDupState('idle');
    setOtpSent(false);
    setOtpVerified(false);
    setOtpError(null);
    setOtpCode('');
  }

  function handleMobileChange(v: string) {
    setField('mobile', formatPhone(v));
    setMobileError(null);
    resetOtp();
  }

  async function handleMobileBlur() {
    const trimmed = form.mobile.trim();
    const digits  = trimmed.replace(/\D/g, '');
    if (!trimmed || trimmed === base.mobile.trim() || digits.length < 10) {
      setMobileDupState('idle');
      return;
    }
    setMobileDupState('checking');
    try {
      const r = await checkUserExists(trimmed);
      setMobileDupState(r.exists ? 'duplicate' : 'ok');
    } catch {
      setMobileDupState('idle');
    }
  }

  async function handleOtpRequest() {
    setOtpLoading(true);
    setOtpError(null);
    try {
      await requestPhoneOtp(form.mobile.trim());
      setOtpSent(true);
    } catch {
      setOtpError('인증번호 발송에 실패했습니다.');
    } finally {
      setOtpLoading(false);
    }
  }

  async function handleOtpVerify(code: string) {
    setOtpLoading(true);
    setOtpError(null);
    try {
      const res = await verifyPhoneOtp({ mobile: form.mobile.trim(), verifyCode: code });
      if (res.verified) { setOtpVerified(true); setMobileError(null); }
      else setOtpError('인증번호가 일치하지 않습니다.');
    } catch {
      setOtpError('인증에 실패했습니다.');
    } finally {
      setOtpLoading(false);
    }
  }

  // ── 저장 — 바뀐 부분(개인정보 · 업체정보 · 내 소개 · 로고)만 차례로 보낸다 ──
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (personalDirty) {
        const updated = await updatePersonalInfo({ memberName: form.memberName.trim(), mobile: form.mobile.trim() });
        queryClient.setQueryData(['profile'], updated);
        void refreshAuthUser();
      }
      if (companyDirty) {
        await updateCompanyInfo({
          phone: form.compPhone.trim() || undefined,
          email: form.compEmail.trim() || undefined,
          // 주소는 바뀐 경우에만 좌표와 함께 보낸다 — 안 바뀌면 서버가 기존 주소·좌표를 유지
          ...(form.compAddress !== base.compAddress
            ? { address: form.compAddress, gpsLat: compGps?.lat ?? null, gpsLng: compGps?.lng ?? null }
            : {}),
          ...(form.compAddressDtl !== base.compAddressDtl ? { addressDtl: form.compAddressDtl.trim() } : {}),
        });
      }
      if ((promoDirty || logoDirty) && promo) {
        if (logoRemoved && !logoFile && promo.logoUrl) await deleteLogo();

        const text: Parameters<typeof updateCompanyPromo>[0] = {};
        if (form.promoTagline !== base.promoTagline) text.promoTagline = form.promoTagline.trim();
        if (form.introMemo    !== base.introMemo)    text.intro        = form.introMemo;
        if (logoFile) text.logo = logoFile;
        if (Object.keys(text).length) await updateCompanyPromo(text);

        // 태그는 서버 값과 비교해 빠진 것 삭제 → 새로 들어온 것 추가
        for (const type of TAG_TYPES) {
          const before = (promo.tags ?? []).filter((t) => t.tagType === type);
          const after  = form.tags[type];
          for (const t of before) {
            if (!after.includes(t.value)) await deleteCompanyPromoTag(t.tagId);
          }
          const beforeValues = before.map((t) => t.value);
          for (const v of after) {
            if (!beforeValues.includes(v)) await addCompanyPromoTag(type, v);
          }
        }
      }
    },
    onSuccess: async () => {
      const [p, pr] = await Promise.all([
        queryClient.fetchQuery({ queryKey: ['profile'], queryFn: fetchProfile, staleTime: 0 }),
        isMaster ? queryClient.fetchQuery({ queryKey: ['company-promo'], queryFn: fetchCompanyPromo, staleTime: 0 }) : null,
      ]);
      syncProfile(p, 'both');
      if (pr) syncPromo(pr, 'both');
      if (logoFile || logoRemoved) setLogoVersion((n) => n + 1);
      setLogoFile(null);
      setLogoRemoved(false);
      resetOtp();
      setSaveError(null);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
      toast.success('저장됐어요');
    },
    onError: async (err: unknown) => {
      const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
      setSaveError(data?.message ?? '저장에 실패했습니다. 다시 시도해주세요.');
      // 일부만 반영됐을 수 있으니 기준값만 서버 값으로 맞춘다(입력 중인 값은 그대로 둔다)
      const [p, pr] = await Promise.all([
        queryClient.fetchQuery({ queryKey: ['profile'], queryFn: fetchProfile, staleTime: 0 }).catch(() => null),
        isMaster ? queryClient.fetchQuery({ queryKey: ['company-promo'], queryFn: fetchCompanyPromo, staleTime: 0 }).catch(() => null) : null,
      ]);
      if (p) syncProfile(p, 'base');
      if (pr) syncPromo(pr, 'base');
    },
  });

  function handleSave() {
    let firstError: string | null = null;
    const nameErr = form.memberName.trim() ? null : '이름을 입력해 주세요';
    let mobileErr: string | null = null;
    if (!form.mobile.trim()) mobileErr = '휴대폰번호를 입력해 주세요';
    else if (mobileChanged && mobileDupState === 'duplicate') mobileErr = '이미 사용 중인 전화번호예요. 다른 번호를 입력해주세요.';
    else if (mobileChanged && !otpVerified) mobileErr = '휴대폰번호를 변경하려면 인증을 완료해주세요.';
    setNameError(nameErr);
    setMobileError(mobileErr);
    if (nameErr) firstError = 'profile-name';
    else if (mobileErr) firstError = 'profile-mobile';
    if (firstError) { document.getElementById(firstError)?.focus(); return; }
    setSaveError(null);
    saveMutation.mutate();
  }

  function handleRevert() {
    setForm(base);
    setCompGps(null);
    setLogoFile(null);
    setLogoRemoved(false);
    resetOtp();
    setNameError(null);
    setMobileError(null);
    setAreaError(null);
    setStyleWarn(null);
    setSaveError(null);
  }

  const roleLabel = isMaster ? '대표 계정' : '직원 계정';
  const compLabel = profile?.compName ?? '';
  const initial   = (profile?.memberName ?? 'U').charAt(0);

  if (isLoading) {
    return (
      <Page width="md">
        <PageHeader title="내 정보 수정" subtitle="프로필 정보를 확인하고 수정할 수 있어요" />
        <Skeleton height={96} className="mb-6" />
        <Skeleton height={380} />
      </Page>
    );
  }

  return (
    <Page width="md">
      <PageHeader title="내 정보 수정" subtitle="프로필 정보를 확인하고 수정할 수 있어요" />

      <div className="flex flex-col gap-md">
        {/* 프로필 — 사장님은 업체 로고(내 프로필 홍보에서 이동, C-16). 대표사진은 개선사항 B에 따라 제외 */}
        <Card>
          <div className="flex items-center gap-md">
            {/* 64px = spacing-10 토큰 */}
            <Avatar src={isMaster ? shownLogo ?? undefined : undefined} alt={isMaster ? '로고' : undefined} className="h-10 w-10 shrink-0 text-heading-md">
              {!(isMaster && shownLogo) && initial}
            </Avatar>
            <div>
              <p className="text-heading-sm font-bold text-text">{profile?.memberName}</p>
              <p className="mt-02 text-caption text-text-muted">
                {roleLabel}{compLabel ? ` · ${compLabel}` : ''}
              </p>
              {isMaster && (
                <div className="mt-04 flex flex-wrap items-center gap-03">
                  {/* D-09 §15.17 파일 입력(버튼형) — 5MB 초과는 컴포넌트가 막고 안내한다 */}
                  <FileInput
                    variant="secondary"
                    size="sm"
                    accept="image/png,image/jpeg"
                    maxSizeMB={5}
                    disabled={promoLoading}
                    onFiles={(files) => handleLogoSelected(files[0])}
                  >
                    로고 변경
                  </FileInput>
                  <Button variant="ghost" size="sm" onClick={handleLogoDelete} disabled={!shownLogo || promoLoading}>
                    삭제
                  </Button>
                  <span className="text-caption text-text-muted">JPG·PNG · 정사각형 권장 · 5MB 이하</span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* 개인정보 · 업체정보 — 개선사항 B: 세로 한 줄 배치를 가로(2열) 배치로.
            업체정보가 없는 직원 계정은 개인정보 1칸만 보인다. */}
        <div className="grid grid-cols-1 items-start gap-md md:grid-cols-2">
          <section className="flex flex-col gap-03">
            <h3 className="text-heading-sm text-text">개인정보</h3>
            <Card>
              <div className="flex flex-col gap-04">
                <LockedField label="아이디" value={profile?.loginId ?? ''} />

                <Input
                  label="이름"
                  required
                  id="profile-name"
                  value={form.memberName}
                  onChange={(e) => { setField('memberName', e.target.value); setNameError(null); }}
                  placeholder="이름을 입력하세요"
                  error={nameError ?? undefined}
                  size="md" width="auto"
                />

                <div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Input
                        label="휴대폰번호"
                        required
                        id="profile-mobile"
                        value={form.mobile}
                        onChange={(e) => handleMobileChange(e.target.value)}
                        onBlur={handleMobileBlur}
                        placeholder="010-0000-0000"
                        error={mobileError ?? undefined}
                        size="md" width="auto"
                      />
                    </div>
                    {mobileReadyForOtp && !otpVerified && (
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={() => void handleOtpRequest()}
                        loading={otpLoading}
                        disabled={mobileDupState === 'checking'}
                        className="shrink-0"
                      >
                        {otpSent ? '재발송' : '인증번호 발송'}
                      </Button>
                    )}
                  </div>
                  {mobileDupState === 'checking' && (
                    <p className="mt-1.5 text-xs text-text-muted">확인 중…</p>
                  )}
                  {mobileDupState === 'duplicate' && !mobileError && (
                    <p className="mt-1.5 text-xs font-semibold text-error">이미 사용 중인 전화번호예요.</p>
                  )}

                  {mobileReadyForOtp && otpSent && !otpVerified && (
                    <div className="mt-2 flex items-end gap-2">
                      <div className="flex-1">
                        <Input
                          label="인증번호"
                          id="profile-mobile-otp"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="인증번호 6자리"
                          size="md" width="auto"
                        />
                      </div>
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={() => void handleOtpVerify(otpCode)}
                        loading={otpLoading}
                        disabled={otpCode.length < 6}
                        className="shrink-0"
                      >
                        확인
                      </Button>
                    </div>
                  )}
                  {mobileReadyForOtp && otpVerified && (
                    <p className="mt-1.5 text-xs font-semibold text-success">휴대폰번호 인증이 완료됐어요.</p>
                  )}
                  {mobileReadyForOtp && otpError && (
                    <p className="mt-1.5 text-xs font-semibold text-error">{otpError}</p>
                  )}
                </div>

                <LockedField label="이메일" value={profile?.email ?? ''} />

                {/* 비밀번호 — SNS 가입 계정은 비밀번호가 없어 미노출 */}
                {!isSns && (
                  <div className="field field-auto">
                    <label>비밀번호</label>
                    <div className="flex h-[var(--control-height-base)] items-center justify-between rounded-md border border-border bg-surface pl-04 pr-02 text-body-sm text-text-muted">
                      <span aria-label="비밀번호 숨김">●●●●●●●●</span>
                      <Button variant="secondary" size="sm" onClick={() => setShowPwModal(true)}>
                        변경
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </section>

          {/* 업체정보 (마스터만) */}
          {isMaster && (
            <section className="flex flex-col gap-03">
              <h3 className="text-heading-sm text-text">업체정보</h3>
              <Card>
                <div className="flex flex-col gap-04">
                  <LockedField label="상호명" value={profile?.compName ?? ''} />
                  <LockedField label="사업자번호" value={profile?.compNo ?? ''} />
                  <LockedField label="대표자명" value={profile?.compRepName ?? ''} />

                  {/* 사업자 주소 — 개선사항 C-19 */}
                  <AddressSearchField
                    label="사업자 주소"
                    value={form.compAddress}
                    onSelect={(r) => {
                      setField('compAddress', r.roadAddress);
                      setCompGps({ lat: r.gpsLat, lng: r.gpsLng });
                      if (!r.roadAddress) setField('compAddressDtl', '');
                    }}
                  />
                  {form.compAddress && (
                    <Input
                      label="상세주소"
                      id="profile-comp-address-dtl"
                      value={form.compAddressDtl}
                      onChange={(e) => setField('compAddressDtl', e.target.value)}
                      placeholder="동·호수 등 상세주소를 입력하세요"
                      maxLength={100}
                      size="md" width="auto"
                    />
                  )}

                  <Input
                    label="업체 전화번호"
                    id="profile-comp-phone"
                    value={form.compPhone}
                    onChange={(e) => setField('compPhone', e.target.value)}
                    placeholder="업체 전화번호를 입력하세요"
                    size="md" width="auto"
                  />

                  <Input
                    label="업체 이메일"
                    id="profile-comp-email"
                    type="email"
                    value={form.compEmail}
                    onChange={(e) => setField('compEmail', e.target.value)}
                    placeholder="업체 이메일을 입력하세요"
                    size="md" width="auto"
                  />

                  <p className="flex items-start gap-02 text-caption text-text-muted">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="mt-01 shrink-0" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>상호명·사업자번호·대표자명 변경은 고객센터로 문의해 주세요</span>
                  </p>
                </div>
              </Card>
            </section>
          )}
        </div>

        {/* 내 소개 — 개선사항 C-16: '내 프로필 홍보'의 내소개 속성을 사장님 내 정보로 통합.
            업체 단위 데이터(TS_COMPANY_PROMO)라 사장님만 노출·수정한다. */}
        {isMaster && (
          <section className="mt-03 flex flex-col gap-03">
            <h3 className="flex items-baseline gap-03 text-heading-sm text-text">
              내 소개
              <span className="text-caption font-normal text-text-muted">고객 홍보 페이지에 보여요</span>
            </h3>
            {/* .app-card 는 overflow:hidden — 시공가능지역 드롭다운 목록이 카드 경계에서 잘리지 않게 넘침 허용 */}
            <Card className="!overflow-visible">
              {promoLoading ? (
                <div className="flex flex-col gap-04">
                  <Skeleton height={48} />
                  <Skeleton height={120} />
                </div>
              ) : (
                <div className="flex flex-col gap-md">
                  <Input
                    label="한줄 소개"
                    id="profile-promo-tagline"
                    value={form.promoTagline}
                    onChange={(e) => setField('promoTagline', e.target.value)}
                    placeholder="예) 정직한 시공, 믿을 수 있는 인테리어"
                    maxLength={200}
                    size="md" width="auto"
                  />

                  <ChipSelect
                    label="시공분야"
                    sub="여러 개"
                    options={SPECIALTY_OPTIONS}
                    values={form.tags.SPECIALTY}
                    onToggle={toggleSpecialty}
                  />

                  {/* 시공가능지역 — 시군구 검색 · 다중선택, 5개 이내(C-17) */}
                  <MultiSelect
                    label={`시공가능지역 (시·구 최대 ${AREA_MAX}개 · ${form.tags.AREA.length}/${AREA_MAX})`}
                    values={form.tags.AREA}
                    onChange={handleAreaChange}
                    options={areaOptions}
                    searchable
                    caretToggle
                    placeholder={form.tags.AREA.length ? '추가 검색' : '시·구 검색 (예: 강남)'}
                    emptyText="검색 결과가 없어요"
                    error={areaError ?? undefined}
                    renderOption={(opt, { selected }) => (
                      <span className="flex w-full items-center gap-03">
                        {opt.label}
                        {!selected && coveredBy(form.tags.AREA, opt.value) && (
                          <small className="ml-auto text-caption text-text-muted">전체에 포함</small>
                        )}
                      </span>
                    )}
                  />

                  <ChipSelect
                    label="디자인스타일"
                    sub={<>최대 {STYLE_MAX}개 · <b className="text-point">{form.tags.STYLE.length}</b>/{STYLE_MAX}</>}
                    options={STYLE_OPTIONS}
                    values={form.tags.STYLE}
                    onToggle={toggleStyle}
                    warn={styleWarn}
                  />

                  {/* 소개메모 = 사장님 자유메모(C-17) — 기존 '사장님 소개'(INTRO_TEXT) 재사용 */}
                  <div className="flex flex-col gap-02">
                    <Textarea
                      label="소개메모"
                      id="profile-promo-intro"
                      value={form.introMemo}
                      onChange={(e) => setField('introMemo', e.target.value)}
                      placeholder="나를 간단히 소개해 보세요 (예: 경력, 강점, 진행 방식)"
                      maxLength={MEMO_MAX}
                      rows={4}
                    />
                    <span className="self-end text-caption text-text-muted">{form.introMemo.length}/{MEMO_MAX}</span>
                  </div>
                </div>
              )}
            </Card>
          </section>
        )}

        {saveError && <Alert kind="error">{saveError}</Alert>}

        {/* 하단 고정 저장 바 — 탈퇴 · 변경 건수 · 되돌리기 · 저장 (V2.6 K. 내 정보) */}
        <div
          className="sticky bottom-05 z-sticky mt-03 flex items-center gap-03 rounded-lg border border-border bg-surface px-05 py-04"
          style={{ boxShadow: 'var(--elevation-3)' }}
        >
          {/* 탈퇴 — 부정 액션이라 etc 로 덜 강조한다 (D-09 §15.1) */}
          <Button
            variant="etc"
            onClick={() => navigate(isMaster ? '/settings/withdraw/master' : '/settings/withdraw/staff')}
          >
            {isMaster ? '마스터 탈퇴' : '직원 탈퇴'}
          </Button>
          <span className={`flex-1 text-right text-body-sm ${justSaved && !changedCount ? 'font-semibold text-success' : 'text-text-muted'}`}>
            {changedCount > 0
              ? <><b className="text-point">{changedCount}</b>개 항목 변경됨</>
              : justSaved ? '저장됐어요' : ''}
          </span>
          {changedCount > 0 && (
            <Button variant="secondary" onClick={handleRevert} disabled={saveMutation.isPending}>
              되돌리기
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!changedCount || saveMutation.isPending}
            loading={saveMutation.isPending}
          >
            {changedCount > 0 ? `저장 (${changedCount})` : '저장'}
          </Button>
        </div>
      </div>

      <PasswordChangeModal open={showPwModal} onClose={() => setShowPwModal(false)} />
    </Page>
  );
}

